import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Construction, ArrowLeft, Clock, Mail, Building2, Home } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { mailto } from '../lib/site';

interface FeatureUnavailableProps {
  /**
   * Title displayed at the top of the page
   * @default "Feature Coming Soon"
   */
  title?: string;
  
  /**
   * Main description text
   * @default "This feature is currently under development."
   */
  description?: string;
  
  /**
   * Optional subtitle for additional context
   */
  subtitle?: string;
  
  /**
   * Type of feature - affects the icon displayed
   * @default "generic"
   */
  featureType?: 'business' | 'service' | 'maintenance' | 'generic';
  
  /**
   * Whether to show the "Go Back" button
   * @default true
   */
  showBackButton?: boolean;
  
  /**
   * Custom back button path (uses navigate(-1) if not provided)
   */
  backPath?: string;
  
  /**
   * Whether to show the home link
   * @default true
   */
  showHomeLink?: boolean;
  
  /**
   * Whether to show the contact support option
   * @default false
   */
  showContactOption?: boolean;
  
  /**
   * Optional expected availability date
   */
  expectedDate?: string;
  
  /**
   * Name of the service for display purposes
   */
  serviceName?: string;

  /**
   * Optional logo image path (e.g. /mono-toolbox.svg) - overrides featureType icon when set
   */
  logoSrc?: string;
}

const FeatureUnavailable: React.FC<FeatureUnavailableProps> = ({
  title = 'Feature Coming Soon',
  description = 'This feature is currently under development.',
  subtitle,
  featureType = 'generic',
  showBackButton = true,
  backPath,
  showHomeLink = true,
  showContactOption = false,
  expectedDate,
  serviceName,
  logoSrc,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  const getIcon = () => {
    switch (featureType) {
      case 'business':
        return <Building2 className="w-20 h-20 text-primary-400" />;
      case 'service':
        return <Clock className="w-20 h-20 text-amber-400" />;
      case 'maintenance':
        return <Construction className="w-20 h-20 text-orange-400" />;
      default:
        return <Construction className="w-20 h-20 text-primary-400" />;
    }
  };

  const getIconBg = () => {
    switch (featureType) {
      case 'business':
        return 'bg-primary-700/80';
      case 'service':
        return 'bg-amber-700/80';
      case 'maintenance':
        return 'bg-orange-700/80';
      default:
        return 'bg-primary-700/80';
    }
  };

  return (
    <div className="page-shell page-section-major">
      <div className="page-inner">
        <div className="bg-secondary-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-primary-700/50 p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
          {/* Icon */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className={`w-28 h-28 sm:w-32 sm:h-32 rounded-2xl flex items-center justify-center shadow-lg overflow-hidden ${logoSrc ? 'bg-secondary-800/80 p-3' : getIconBg()}`}>
                  {logoSrc ? (
                    <img src={logoSrc} alt="" className="w-full h-full object-contain" />
                  ) : (
                    getIcon()
                  )}
                </div>
              </div>
            </div>

            {/* Service Name Badge */}
            {serviceName && (
              <div className="inline-block px-4 py-1 bg-secondary-800/80 rounded-full mb-4">
                <span className="text-sm font-medium text-secondary-300">{serviceName}</span>
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">{title}</h1>
            
            {/* Subtitle */}
            {subtitle && (
              <p className="text-lg text-primary-400 font-medium mb-2">{subtitle}</p>
            )}

            {/* Description */}
            <p className="text-secondary-400 leading-relaxed">{description}</p>

            {/* Expected Date */}
            {expectedDate && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-secondary-800/50 rounded-lg border border-primary-700/50">
                <Clock className="w-4 h-4 text-primary-400" />
                <span className="text-sm text-secondary-300">Expected: {expectedDate}</span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-primary-700/30"></div>

          {/* Actions */}
          <div className="space-y-3">
            {showBackButton && (
              <Button onClick={handleBack} fullWidth className="justify-center">
                <ArrowLeft className="w-5 h-5" />
                Go Back
              </Button>
            )}

            {showHomeLink && (
              <Link
                to="/"
                className="w-full py-3 px-4 bg-secondary-800/50 border border-primary-700/50 text-secondary-300 font-medium rounded-lg hover:bg-secondary-800 hover:border-primary-600/50 transition-all flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                Return to Home
              </Link>
            )}

            {showContactOption && (
              <a
                href={mailto(`Interest in ${serviceName || 'Feature'}`)}
                className="w-full py-3 px-4 bg-secondary-800/50 border border-primary-700/50 text-secondary-300 font-medium rounded-lg hover:bg-secondary-800 hover:border-primary-600/50 transition-all flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Contact Us
              </a>
            )}
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-secondary-500">
              Thank you for your patience. We're working hard to bring you this feature.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureUnavailable;
